import React from 'react';
import AdminPageLoader from '../../../shared/ui/admin-loader/AdminPageLoader';
import { useQuickPlacementTestForm } from '../lib/useQuickPlacementTestForm';

const inputStyle = {
  width: '100%',
  padding: '10px',
  border: '1px solid #ccc',
  borderRadius: '6px',
  fontFamily: 'inherit',
};

const SectionBox = ({ title, description, children }) => (
  <div style={{ marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #ccc' }}>
    <div style={{ marginBottom: '16px' }}>
      <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px' }}>{title}</h3>
      {description && <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>{description}</p>}
    </div>
    {children}
  </div>
);

const LocalizedPair = ({ label, hint, valueUa, valueEn, onChangeUa, onChangeEn, isTextarea = false, rows = 3 }) => (
  <div style={{ marginBottom: '16px' }}>
    {hint && <p style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>{hint}</p>}
    {label && (
      <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '8px' }}>{label}</label>
    )}
    <div className="form-wrapper-2-column">
      <div>
        <label style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: '4px' }}>Українська (UA)</label>
        {isTextarea ? (
          <textarea
            value={valueUa}
            onChange={(e) => onChangeUa(e.target.value)}
            className="custom-textarea"
            rows={rows}
            placeholder="Текст українською..."
          />
        ) : (
          <input type="text" value={valueUa} onChange={(e) => onChangeUa(e.target.value)} placeholder="Текст українською..." style={inputStyle} />
        )}
      </div>
      <div>
        <label style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: '4px' }}>English (EN)</label>
        {isTextarea ? (
          <textarea
            value={valueEn}
            onChange={(e) => onChangeEn(e.target.value)}
            className="custom-textarea"
            rows={rows}
            placeholder="English text..."
          />
        ) : (
          <input type="text" value={valueEn} onChange={(e) => onChangeEn(e.target.value)} placeholder="English text..." style={inputStyle} />
        )}
      </div>
    </div>
  </div>
);

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const QuickPlacementTestForm = ({ initialData, templateId }) => {
  const formik = useQuickPlacementTestForm(initialData, templateId);
  const questions = formik.values.questionPool || [];

  const updateQuestions = (updater) => {
    const next = questions.map((question) => ({
      ...question,
      options: [...(question.options || [])],
    }));
    updater(next);
    formik.setFieldValue('questionPool', next);
  };

  const addQuestion = () => {
    // ID питання — ключ, за яким відповідь потрапляє в заявку, тож він має бути
    // унікальним навіть після видалення попередніх питань.
    const usedIds = new Set(questions.map((question) => question.id));
    let index = questions.length + 1;
    while (usedIds.has(`question-${index}`)) {
      index += 1;
    }
    const baseId = `question-${index}`;
    formik.setFieldValue('questionPool', [
      ...questions,
      {
        id: baseId,
        groupId: `${baseId}-group`,
        prompt: { ua: '', en: '' },
        options: [
          { id: `${baseId}-option-1`, label: 'A', text: { ua: '', en: '' } },
          { id: `${baseId}-option-2`, label: 'B', text: { ua: '', en: '' } },
        ],
      },
    ]);
  };

  return (
    <form id="quick-placement-test-form" onSubmit={formik.handleSubmit} className="programs-create-form">
      {formik.isSubmitting && <AdminPageLoader label="Збереження міні-тесту..." fullScreen />}

      <div
        style={{
          marginBottom: '24px',
          padding: '14px 16px',
          background: '#EEF4FF',
          border: '1px solid #C7D8FF',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#1E3A8A',
        }}
      >
        Міні-тест на головній — це <strong>опитування для заявки</strong>, а не тест.
        Правильних відповідей у нього немає, бали й рівень не рахуються: відповіді
        просто йдуть у заявку менеджеру. Тут редагуються лише тексти питань і варіантів.
      </div>

      {/* Basic Info */}
      <SectionBox title="📋 Основна інформація" description="Назва та ідентифікатор міні-тесту">
        <LocalizedPair
          label="Назва шаблону"
          hint="Наприклад: Quick Placement Test"
          valueUa={formik.values.title?.ua || ''}
          valueEn={formik.values.title?.en || ''}
          onChangeUa={(value) => formik.setFieldValue('title.ua', value)}
          onChangeEn={(value) => formik.setFieldValue('title.en', value)}
        />

        <div style={{ marginBottom: '16px' }}>
          <p style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>
            Технічний ідентифікатор. Сайт шукає міні-тест саме за ним — змінюйте лише свідомо.
          </p>
          <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '8px' }}>
            Slug (ідентифікатор)
          </label>
          <input
            type="text"
            value={formik.values.slug || ''}
            onChange={(e) => formik.setFieldValue('slug', slugify(e.target.value))}
            placeholder="quick-placement-test-ubc"
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formik.values.isActive !== false}
              onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span>Активний шаблон</span>
          </label>
        </div>
      </SectionBox>

      {/* Questions */}
      <SectionBox
        title="❓ Питання"
        description={`Показуються на головній по порядку. Всього: ${questions.length}`}
      >
        {questions.map((question, questionIndex) => (
          <div
            key={question.id || questionIndex}
            style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px', border: '1px solid #ddd' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ fontSize: '13px', fontWeight: '600', margin: 0 }}>
                Питання {questionIndex + 1}
                <span style={{ fontWeight: '400', color: '#94A3B8' }}> (ID: {question.id})</span>
              </h4>
              <button
                type="button"
                onClick={() =>
                  formik.setFieldValue(
                    'questionPool',
                    questions.filter((_, index) => index !== questionIndex)
                  )
                }
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#FE0707',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '11px',
                }}
              >
                Видалити питання
              </button>
            </div>

            <LocalizedPair
              label="Текст питання"
              valueUa={question.prompt?.ua || ''}
              valueEn={question.prompt?.en || ''}
              onChangeUa={(value) =>
                updateQuestions((next) => {
                  next[questionIndex].prompt = { ...next[questionIndex].prompt, ua: value };
                })
              }
              onChangeEn={(value) =>
                updateQuestions((next) => {
                  next[questionIndex].prompt = { ...next[questionIndex].prompt, en: value };
                })
              }
              isTextarea
              rows={2}
            />

            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #ddd' }}>
              <p style={{ fontSize: '12px', fontWeight: '600', margin: '0 0 8px' }}>
                Варіанти відповіді ({(question.options || []).length})
              </p>

              {(question.options || []).map((option, optionIndex) => (
                <div
                  key={option.id || optionIndex}
                  style={{ marginBottom: '10px', padding: '10px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #eee' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#64748B' }}>
                      Варіант {option.label || OPTION_LABELS[optionIndex] || optionIndex + 1}
                    </span>
                    {(question.options || []).length > 2 && (
                      <button
                        type="button"
                        onClick={() =>
                          updateQuestions((next) => {
                            next[questionIndex].options = next[questionIndex].options.filter(
                              (_, index) => index !== optionIndex
                            );
                          })
                        }
                        style={{
                          padding: '2px 8px',
                          backgroundColor: '#FE0707',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '10px',
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <LocalizedPair
                    valueUa={option.text?.ua || ''}
                    valueEn={option.text?.en || ''}
                    onChangeUa={(value) =>
                      updateQuestions((next) => {
                        next[questionIndex].options[optionIndex] = {
                          ...next[questionIndex].options[optionIndex],
                          text: { ...next[questionIndex].options[optionIndex].text, ua: value },
                        };
                      })
                    }
                    onChangeEn={(value) =>
                      updateQuestions((next) => {
                        next[questionIndex].options[optionIndex] = {
                          ...next[questionIndex].options[optionIndex],
                          text: { ...next[questionIndex].options[optionIndex].text, en: value },
                        };
                      })
                    }
                  />
                </div>
              ))}

              <button
                type="button"
                onClick={() =>
                  updateQuestions((next) => {
                    const options = next[questionIndex].options;
                    const nextIndex = options.length;
                    options.push({
                      id: `${next[questionIndex].id}-option-${nextIndex + 1}`,
                      label: OPTION_LABELS[nextIndex] || String(nextIndex + 1),
                      text: { ua: '', en: '' },
                    });
                  })
                }
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#2D9E6B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '11px',
                }}
              >
                + Додати варіант
              </button>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={addQuestion}
          style={{
            padding: '10px 16px',
            backgroundColor: '#2D9E6B',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            marginTop: '12px',
          }}
        >
          + Додати питання
        </button>
      </SectionBox>

      {/* Errors */}
      {Object.keys(formik.errors).length > 0 && (
        <div
          style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '6px',
          }}
        >
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#c00', marginBottom: '12px', marginTop: 0 }}>
            ⚠️ Помилки валідації:
          </h4>
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#c00' }}>
            {Object.entries(formik.errors).map(([key, value]) => (
              <li key={key} style={{ marginBottom: '6px' }}>
                <strong>{key}:</strong> {typeof value === 'string' ? value : JSON.stringify(value)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  );
};

export default QuickPlacementTestForm;
